import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Loader2, Users, Mail } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";

export default function AdminWaitlist() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (user && user.role !== "admin") {
      setForbidden(true);
      return;
    }
    if (user) {
      api
        .get("/admin/waitlist")
        .then(({ data }) => setData(data))
        .catch((e) => {
          if (e.response?.status === 403) setForbidden(true);
          else toast.error(formatApiError(e.response?.data?.detail));
        });
    }
  }, [user]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-US", {
        year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const exportCsv = () => {
    if (!data?.subscribers?.length) return;
    const rows = [["Email", "Name", "Joined"]];
    data.subscribers.forEach((s) => rows.push([s.email, s.name || "", s.created_at || ""]));
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (forbidden) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-sand text-ink gap-4" data-testid="admin-forbidden">
        <p className="font-serif text-3xl">This area is for the studio admin.</p>
        <button onClick={() => navigate("/dashboard")} className="rounded-full bg-emerald text-white px-6 py-3 text-sm hover:bg-emerald/90 transition-colors duration-300">
          Back to dashboard
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand">
        <Loader2 className="w-7 h-7 animate-spin text-emerald" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand text-ink">
      <header className="px-6 sm:px-10 lg:px-16 py-6 flex items-center justify-between border-b border-ink/10">
        <div className="font-serif text-2xl">Élanora</div>
        <button onClick={() => navigate("/dashboard")} data-testid="admin-back"
          className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink transition-colors duration-300">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Dashboard
        </button>
      </header>

      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="eyebrow mb-3">Studio admin</p>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <h1 className="text-4xl sm:text-5xl tracking-tight">Early-access waitlist</h1>
              <div className="flex items-center gap-2 mt-4 text-ink/60">
                <Users className="w-5 h-5 text-emerald" strokeWidth={1.5} />
                <span data-testid="waitlist-count">{data.count} {data.count === 1 ? "subscriber" : "subscribers"}</span>
              </div>
            </div>
            <button onClick={exportCsv} disabled={!data.count} data-testid="export-csv-btn"
              className="inline-flex items-center gap-2 rounded-full bg-emerald text-white px-6 py-3 text-sm hover:bg-emerald/90 transition-colors duration-300 disabled:opacity-40 self-start sm:self-auto">
              <Download className="w-4 h-4" strokeWidth={1.5} /> Export CSV
            </button>
          </div>
        </motion.div>

        <div className="mt-10 border border-ink/10 rounded-[2px] overflow-hidden" data-testid="waitlist-table">
          {data.count === 0 ? (
            <div className="p-12 text-center text-ink/50 flex flex-col items-center gap-3">
              <Mail className="w-8 h-8" strokeWidth={1.5} />
              <p className="text-lg">No signups yet. Share your landing page to start the list.</p>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#F2EFEB] text-xs uppercase tracking-widest text-ink/50">
                  <th className="px-5 py-4 font-medium w-12">#</th>
                  <th className="px-5 py-4 font-medium">Email</th>
                  <th className="px-5 py-4 font-medium">Name</th>
                  <th className="px-5 py-4 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {data.subscribers.map((s, i) => (
                  <tr key={s.email + i} className="border-t border-ink/10 hover:bg-[#F2EFEB]/60 transition-colors duration-200" data-testid={`waitlist-row-${i}`}>
                    <td className="px-5 py-4 text-ink/40">{i + 1}</td>
                    <td className="px-5 py-4 font-medium">{s.email}</td>
                    <td className="px-5 py-4 text-ink/70">{s.name || "—"}</td>
                    <td className="px-5 py-4 text-ink/60 text-sm">{fmtDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
